import { IntegrationTypeConfiguration } from "../../types";

export interface KeyValuePair {
  id: string;
  key: string;
  value: string;
}

/**
 * Converts API format [{"key1": "value1"}, {"key2": "value2"}] 
 * to internal format [{id: "...", key: "key1", value: "value1"}, ...]
 */
export const convertFromApiFormat = (
  apiData: any[],
  configName: string
): KeyValuePair[] => {
  if (!Array.isArray(apiData)) {
    return [];
  }

  let idCounter = 0;
  return apiData.map((item, idx) => {
    if (typeof item === "object" && item !== null) {
      // Extract key and value from object like {"key1": "value1"}
      const entries = Object.entries(item);
      if (entries.length > 0) {
        const [k, v] = entries[0];
        return {
          id: `pair-${configName}-${idx}-${Date.now()}-${idCounter++}-${Math.random().toString(36).substr(2, 9)}`,
          key: k,
          value: String(v),
        };
      }
    }
    // Fallback: if already in internal format with key/value, preserve it
    if (typeof item === "object" && item !== null && "key" in item && "value" in item) {
      return {
        ...item,
        id: item.id || `pair-${configName}-${idx}-${Date.now()}-${idCounter++}-${Math.random().toString(36).substr(2, 9)}`,
      };
    }
    // Fallback for unexpected format
    return {
      id: `pair-${configName}-${idx}-${Date.now()}-${idCounter++}-${Math.random().toString(36).substr(2, 9)}`,
      key: "",
      value: "",
    };
  });
};

/**
 * Converts internal format [{id: "...", key: "key1", value: "value1"}, ...]
 * to API format [{"key1": "value1"}, {"key2": "value2"}]
 */
export const convertToApiFormat = (
  internalData: KeyValuePair[]
): Array<Record<string, string>> => {
  if (!Array.isArray(internalData)) {
    return [];
  }

  return internalData
    .filter((item) => item?.key && String(item.key).trim() !== "")
    .map((item) => {
      const keyStr = String(item.key);
      const valueStr = String(item.value || "");
      return { [keyStr]: valueStr };
    });
};

/**
 * Converts all configurations to API format based on their types
 */
export const convertConfigurationsToApiFormat = (
  configurations: Record<string, any>,
  typeConfigurations: IntegrationTypeConfiguration[]
): Record<string, any> => {
  const apiFormat: Record<string, any> = {};

  Object.keys(configurations).forEach((key) => {
    const config = typeConfigurations.find((c) => c.name === key);
    const value = configurations[key];

    if (config?.type === "array") {
      apiFormat[key] = convertToApiFormat(value);
    } else {
      apiFormat[key] = value;
    }
  });

  return apiFormat;
};

/**
 * Converts API response configurations to internal format
 */
export const convertConfigurationsFromApiFormat = (
  userInputs: Record<string, any>,
  typeConfigurations: IntegrationTypeConfiguration[]
): Record<string, any> => {
  const convertedConfigs: Record<string, any> = {};

  Object.keys(userInputs).forEach((key) => {
    const config = typeConfigurations.find((c) => c.name === key);
    const value = userInputs[key];

    if (config?.type === "array") {
      if (Array.isArray(value)) {
        convertedConfigs[key] = convertFromApiFormat(value, key);
      } else if (value && typeof value === "object" && !Array.isArray(value)) {
        // Single object format: { "key": "value" } - convert to array
        convertedConfigs[key] = convertFromApiFormat([value], key);
      } else {
        convertedConfigs[key] = [];
      }
    } else {
      convertedConfigs[key] = value;
    }
  });

  return convertedConfigs;
};

